import debug from './debug'
import utilString from './string'

/**
 * 型変換ユーティリティ
 */
export default {
  /**
   * null、undefinedの場合true
   *
   * @param value 確認したい値
   */
  isNullOrUndef (value) {
    if (value === undefined) {
      return true
    }

    if (value === null) {
      return true
    }

    return false
  },

  /**
   * 文字列型に変換
   *
   * @param value 値
   * @returns 変換後の値
   */
  string (value) {
    if (utilString.isEmptyOrUndef(value)) {
      return null
    }

    return String(value)
  },

  /**
   * 数値型に変換
   * 変換できない場合はnull
   *
   * @param value 値
   * @returns 変換後の値
   */
  int (value) {
    if (utilString.isEmptyOrUndef(value)) {
      return null
    }

    const newValue = parseInt(value)
    if (Number.isNaN(newValue)) {
      return null
    }

    return newValue
  },

  /**
   * 浮動小数点型に変換
   * 変換できない場合はnull
   *
   * @param value 値
   * @returns 変換後の値
   */
  float (value, decmalDigit) {
    if (utilString.isEmptyOrUndef(value)) {
      return null
    }

    let text
    if (utilString.isEmptyOrUndef(decmalDigit)) {
      text = value
    } else {
      let buff = (String(value) || '').split('.')
      if (buff.length === 2 && buff[1].length > decmalDigit) {
        buff[1] = buff[1].substr(0, decmalDigit)
      }
      text = `${buff[0]}.${buff[1]}`
    }

    const newValue = parseFloat(text)
    if (Number.isNaN(newValue)) {
      return null
    }

    return newValue
  },

  /**
   * 日付型に変換
   *
   * @param yearOrValue 年 or 文字列（yyyyMMdd）or 文字列（yyyy-MM-dd） or 日付型に変換可能な文字列
   * @param month 月
   * @param day 日
   * @returns 変換後の値
   */
  date (yearOrValue, month, day) {
    if (utilString.isEmptyOrUndef(yearOrValue)) {
      return null
    }
    if (yearOrValue instanceof Date) {
      return yearOrValue
    }

    let y = null
    let m = null
    let d = null
    if (utilString.isEmptyOrUndef(month) || utilString.isEmptyOrUndef(day)) {
      // 値指定
      let buff = this.string(yearOrValue)
      if (buff !== null) {
        if (buff.length === 8) {
          y = this.int(buff.substring(0, 4))
          m = this.int(buff.substring(4, 6))
          d = this.int(buff.substring(6, 8))
        } else if (buff.length === 10) {
          y = this.int(buff.substring(0, 4))
          m = this.int(buff.substring(5, 7))
          d = this.int(buff.substring(8, 10))
        }
      } else {
        try {
          return new Date(yearOrValue)
        } catch (e) {
          debug.error(e)
        }
      }
    } else {
      // 年月日を個別に指定
      y = this.int(yearOrValue)
      m = this.int(month)
      d = this.int(day)
    }
    if (y === null || m === null || d === null) {
      return null
    }

    return new Date(y, m - 1, d)
  },

  /**
   * 日付文字列(APIの書式)に変換
   *
   * @param yearOrValue 年 or 日付型 or 文字列（yyyyMMdd）or 文字列（yyyy-MM-dd） or 日付型に変換可能な文字列
   * @param month 月
   * @param day 日
   * @returns 変換後の値
   */
  dateString (yearOrValue, month, day) {
    let date = this.date(yearOrValue, month, day)
    if (!date) {
      return null
    }

    const y = date.getFullYear()
    const m = date.getMonth() + 1
    const d = date.getDate()

    return `${('0000' + y).slice(-4)}${('00' + m).slice(-2)}${('00' + d).slice(-2)}`
  },

  /**
   * 日付文字列(画面表示の書式)に変換
   *
   * @param yearOrValue 年 or 日付型 or 文字列（yyyyMMdd）or 文字列（yyyy-MM-dd） or 日付型に変換可能な文字列
   * @param month 月
   * @param day 日
   * @returns 変換後の値
   */
  dateDisplay (yearOrValue, month, day) {
    let date = this.date(yearOrValue, month, day)
    if (!date) {
      return null
    }

    const y = date.getFullYear()
    const m = date.getMonth() + 1
    const d = date.getDate()

    return `${('0000' + y).slice(-4)}/${('00' + m).slice(-2)}/${('00' + d).slice(-2)}`
  },

  /**
   * 日付文字列(APIの書式)に変換
   *
   * @param yearOrValue 年 or 日付型 or 文字列（yyyyMMdd）or 文字列（yyyy-MM-dd） or 日付型に変換可能な文字列
   * @param month 月
   * @param day 日
   * @returns 変換後の値
   */
  datePickerString (yearOrValue, month, day) {
    let date = this.date(yearOrValue, month, day)
    if (!date) {
      return null
    }

    const y = date.getFullYear()
    const m = date.getMonth() + 1
    const d = date.getDate()

    return `${('0000' + y).slice(-4)}-${('00' + m).slice(-2)}-${('00' + d).slice(-2)}`
  },

  /**
   * 日付型に変換
   * ※年月を指定で、日付型の日は1日固定
   *
   * @param yearOrValue 年 or 文字列（yyyyMM）or 文字列（yyyy-MM） or 日付型に変換可能な文字列
   * @param month 月
   * @param day 日
   * @returns 変換後の値
   */
  dateYM (yearOrValue, month) {
    if (utilString.isEmptyOrUndef(yearOrValue)) {
      return null
    }
    if (yearOrValue instanceof Date) {
      return yearOrValue
    }

    let y = null
    let m = null
    if (utilString.isEmptyOrUndef(month)) {
      // 値指定
      let buff = this.string(yearOrValue)
      if (buff !== null) {
        if (buff.length === 6) {
          y = this.int(buff.substring(0, 4))
          m = this.int(buff.substring(4, 6))
        } else if (buff.length === 7) {
          y = this.int(buff.substring(0, 4))
          m = this.int(buff.substring(5, 7))
        }
      } else {
        try {
          return new Date(yearOrValue)
        } catch (e) {
          debug.error(e)
        }
      }
    } else {
      // 年月日を個別に指定
      y = this.int(yearOrValue)
      m = this.int(month)
    }
    if (y === null || m === null) {
      return null
    }

    return new Date(y, m - 1, 1)
  },

  /**
   * 日付文字列(APIの書式)に変換
   *
   * @param yearOrValue 年 or 日付型 or 文字列（yyyyMM）or 文字列（yyyy-MM） or 日付型に変換可能な文字列
   * @param month 月
   * @param day 日
   * @returns 変換後の値
   */
  dateYMString (yearOrValue, month) {
    let date = this.dateYM(yearOrValue, month)
    if (!date) {
      return null
    }

    const y = date.getFullYear()
    const m = date.getMonth() + 1

    return `${('0000' + y).slice(-4)}${('00' + m).slice(-2)}`
  },

  /**
   * 日付型（時間のみ有効で、日付はシステム日付）に変換
   *
   * @param hourOrValue 時間 or 数字（hmm）or 数字（hhmm） or 文字列（hhmm）or 文字列（hh:mm） or 日付型に変換可能な文字列
   * @param minutes 分
   * @returns 変換後の値
   */
  time (hourOrValue, minutes) {
    if (utilString.isEmptyOrUndef(hourOrValue)) {
      return null
    }
    if (hourOrValue instanceof Date) {
      return hourOrValue
    }

    let h = null
    let m = null
    if (utilString.isEmptyOrUndef(minutes)) {
      // 値指定
      let buff = this.string(hourOrValue)
      if (buff !== null) {
        if (buff.length === 3 && this.int(buff) !== null) {
          buff = ('0000' + this.int(buff)).slice(-4)
        }
        if (buff.length === 4) {
          h = this.int(buff.substring(0, 2))
          m = this.int(buff.substring(2, 4))
        } else if (buff.length === 5) {
          h = this.int(buff.substring(0, 2))
          m = this.int(buff.substring(3, 5))
        }
      } else {
        try {
          return new Date(hourOrValue)
        } catch (e) {
          debug.error(e)
        }
      }
    } else {
      // 時分を個別に指定
      h = this.int(hourOrValue)
      m = this.int(minutes)
    }
    if (h === null || m === null) {
      return null
    }

    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDay(), h, m, 0)
  },

  /**
   * 時間文字列(APIの書式)に変換
   *
   * @param hourOrValue 時間 or 文字列（hhmm）or 文字列（hh:mm） or 日付型に変換可能な文字列
   * @param minutes 分
   * @returns 変換後の値
   */
  timeString (hourOrValue, minutes) {
    let time = this.time(hourOrValue, minutes)
    if (!time) {
      return null
    }

    const h = time.getHours()
    const m = time.getMinutes()

    return `${('00' + h).slice(-2)}${('00' + m).slice(-2)}`
  },

  /**
   * 時間文字列(画面表示の書式)に変換
   *
   * @param hourOrValue 時間 or 文字列（hhmm）or 文字列（hh:mm） or 日付型に変換可能な文字列
   * @param minutes 分
   * @returns 変換後の値
   */
  timeDisplay (hourOrValue, minutes) {
    let time = this.time(hourOrValue, minutes)
    if (!time) {
      return null
    }

    const h = time.getHours()
    const m = time.getMinutes()

    return `${('00' + h).slice(-2)}:${('00' + m).slice(-2)}`
  },

  /**
   * 日付型に変換
   *
   * @param yearOrValue 年 or 文字列（yyyyMMddhhmm）or 文字列（yyyy-MM-dd hh:mm） or 日付型に変換可能な文字列
   * @param monthOrTime 月 or 文字列（hhmm）or 文字列（hh:mm） or 日付型（時間のみ有効）に変換可能な文字列
   * @param day 日
   * @param hour 時間
   * @param minutes 分
   * @returns 変換後の値
   */
  datetime (yearOrValue, monthOrTime, day, hour, minutes) {
    if (utilString.isEmptyOrUndef(yearOrValue)) {
      return null
    }

    let y = null
    let m = null
    let d = null
    let hh = null
    let mm = null
    if (utilString.isEmptyOrUndef(day) || utilString.isEmptyOrUndef(hour) || utilString.isEmptyOrUndef(minutes)) {
      let date = null
      let time = null
      if (utilString.isEmptyOrUndef(monthOrTime)) {
        // 日時の値を指定
        if (yearOrValue instanceof Date) {
          return yearOrValue
        }

        let buff = this.string(yearOrValue)
        if (buff !== null) {
          if (buff.length === 12) {
            date = this.date(buff.substring(0, 8))
            time = this.time(buff.substring(8, 12))
          } else if (buff.length === 10) {
            date = this.date(buff.substring(0, 10))
            time = this.time(buff.substring(11, 16))
          }
        } else {
          try {
            return new Date(yearOrValue)
          } catch (e) {
            debug.error(e)
          }
        }
      } else {
        // 日付の値と、時間の値を指定
        if (yearOrValue instanceof Date) {
          date = yearOrValue
        } else {
          let buffDate = this.string(yearOrValue)
          if (buffDate !== null) {
            date = this.date(buffDate)
          } else {
            date = this.date(yearOrValue)
          }
        }

        if (monthOrTime instanceof Date) {
          time = monthOrTime
        } else {
          let buffTime = this.string(monthOrTime)
          if (buffTime !== null) {
            time = this.time(buffTime)
          } else {
            time = this.time(monthOrTime)
          }
        }
      }

      if (date === null || time === null) {
        return null
      }
      y = date.getFullYear()
      m = date.getMonth() + 1
      d = date.getDate()
      hh = time.getHours()
      mm = time.getMinutes()
    } else {
      // 年月日時分を個別に指定
      y = this.int(yearOrValue)
      m = this.int(monthOrTime)
      d = this.int(day)
      hh = this.int(hour)
      mm = this.int(minutes)
    }
    if (y === null || m === null || d === null || hh === null || mm === null) {
      return null
    }

    return new Date(y, m - 1, d, hh, mm)
  },

  /**
   * 日付文字列(APIの書式)に変換
   *
   * @param yearOrValue 年 or 日付型 or 文字列（yyyyMMddhhmm）or 文字列（yyyy-MM-dd hh:mm） or 日付型に変換可能な文字列
   * @param monthOrTime 月 or 文字列（hhmm）or 文字列（hh:mm） or 日付型（時間のみ有効）に変換可能な文字列
   * @param day 日
   * @param hour 時間
   * @param minutes 分
   * @returns 変換後の値
   */
  datetimeString (yearOrValue, monthOrTime, day, hour, minutes) {
    let buff = this.datetime(yearOrValue, monthOrTime, day, hour, minutes)
    if (!buff) {
      return null
    }

    const y = buff.getFullYear()
    const m = buff.getMonth() + 1
    const d = buff.getDate()
    const hh = buff.getHours()
    const mm = buff.getMinutes()

    return `${('0000' + y).slice(-4)}${('00' + m).slice(-2)}${('00' + d).slice(-2)}${('00' + hh).slice(-2)}${('00' + mm).slice(-2)}`
  }
}
